export interface ClaimAudio {
  id: string;
  matchId: string;
  roundId: string;
  playerId: string;
  playerName: string;
  word: string;
  sentence: string;
  transcript: string;
  mimeType: string;
  durationMs: number;
  createdAtMs: number;
  audio: Blob;
}

export type ClaimAudioMetadata = Omit<ClaimAudio, 'audio'>;

const DB_NAME = 'amarkosha-fpp-audio';
const DB_VERSION = 1;
const STORE_NAME = 'claims';
let sharedAudioElement: HTMLAudioElement | null = null;
let sharedAudioUrl: string | null = null;

function openAudioDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error ?? new Error('Unable to open audio storage.'));
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('matchId', 'matchId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Audio storage operation failed.'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Audio storage transaction failed.'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Audio storage transaction was aborted.'));
  });
}

export async function saveClaimAudio(claim: ClaimAudio): Promise<void> {
  const db = await openAudioDb();
  try {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const done = transactionDone(transaction);
    transaction.objectStore(STORE_NAME).put(claim);
    await done;
  } finally {
    db.close();
  }
}

export async function listMatchAudio(matchId: string): Promise<ClaimAudio[]> {
  const db = await openAudioDb();
  try {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const done = transactionDone(transaction);
    const index = transaction.objectStore(STORE_NAME).index('matchId');
    const claims = await requestResult(index.getAll(IDBKeyRange.only(matchId))) as ClaimAudio[];
    await done;
    return claims.sort((a, b) => a.createdAtMs - b.createdAtMs);
  } finally {
    db.close();
  }
}

export async function clearMatchAudio(matchId: string): Promise<void> {
  const db = await openAudioDb();
  try {
    const readTransaction = db.transaction(STORE_NAME, 'readonly');
    const readDone = transactionDone(readTransaction);
    const index = readTransaction.objectStore(STORE_NAME).index('matchId');
    const keys = await requestResult(index.getAllKeys(IDBKeyRange.only(matchId)));
    await readDone;
    const writeTransaction = db.transaction(STORE_NAME, 'readwrite');
    const writeDone = transactionDone(writeTransaction);
    const store = writeTransaction.objectStore(STORE_NAME);
    keys.forEach((key) => store.delete(key));
    await writeDone;
  } finally {
    db.close();
  }
}

export function preferredAudioMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/ogg;codecs=opus',
    'audio/webm',
  ];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? '';
}

function encodeMonoWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeAscii = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) view.setUint8(offset + i, value.charCodeAt(i));
  };
  writeAscii(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeAscii(8, 'WAVE');
  writeAscii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

function mixAndResample(audio: AudioBuffer, targetRate: number): Float32Array {
  const sourceLength = audio.length;
  const mono = new Float32Array(sourceLength);
  for (let channel = 0; channel < audio.numberOfChannels; channel += 1) {
    const data = audio.getChannelData(channel);
    for (let i = 0; i < sourceLength; i += 1) mono[i] += data[i] / audio.numberOfChannels;
  }
  if (audio.sampleRate === targetRate) return mono;
  const targetLength = Math.max(1, Math.round(sourceLength * targetRate / audio.sampleRate));
  const output = new Float32Array(targetLength);
  const ratio = audio.sampleRate / targetRate;
  for (let i = 0; i < targetLength; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(sourceLength, Math.max(start + 1, Math.floor((i + 1) * ratio)));
    let sum = 0;
    for (let j = start; j < end; j += 1) sum += mono[j];
    output[i] = sum / (end - start);
  }
  return output;
}

export async function toPortableWav(blob: Blob): Promise<Blob> {
  const AudioContextCtor = window.AudioContext
    ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) return blob;
  const context = new AudioContextCtor();
  try {
    const decoded = await context.decodeAudioData(await blob.arrayBuffer());
    return encodeMonoWav(mixAndResample(decoded, 16_000), 16_000);
  } finally {
    await context.close();
  }
}

export async function isPortableWav(blob: Blob): Promise<boolean> {
  if (blob.size < 12 || blob.type !== 'audio/wav') return false;
  const header = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  const ascii = String.fromCharCode(...header);
  return ascii.startsWith('RIFF') && ascii.slice(8, 12) === 'WAVE';
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

export function base64ToBlob(value: string, mimeType: string): Blob {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

function audioElement(): HTMLAudioElement {
  if (!sharedAudioElement) {
    sharedAudioElement = new Audio();
    sharedAudioElement.preload = 'auto';
  }
  return sharedAudioElement;
}

export async function unlockAudioPlayback(): Promise<void> {
  const audio = audioElement();
  audio.muted = true;
  audio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
  try {
    await audio.play();
    audio.pause();
  } finally {
    audio.removeAttribute('src');
    audio.muted = false;
  }
}

export function playAudioBlob(blob: Blob): Promise<void> {
  const audio = audioElement();
  if (blob.type && audio.canPlayType(blob.type) === '') {
    return Promise.reject(new Error(`This device cannot play ${blob.type}.`));
  }
  if (sharedAudioUrl) URL.revokeObjectURL(sharedAudioUrl);
  sharedAudioUrl = URL.createObjectURL(blob);
  const release = () => {
    if (sharedAudioUrl) URL.revokeObjectURL(sharedAudioUrl);
    sharedAudioUrl = null;
  };
  audio.src = sharedAudioUrl;
  audio.onended = release;
  audio.onerror = release;
  return audio.play().catch((error: unknown) => {
    release();
    throw error;
  });
}
